/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, {
    useState,
    useMemo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    forwardRef,
} from 'react';
import {
    Table,
    type TableProps,
    type ColumnType,
    type ColumnsType,
} from '@app/ui';
import './table.scss';

const MIN_WIDTH = 50;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 150;

type AnyColumn = ColumnType<any> & { children?: AnyColumn[] };

// Stable identity for a column. Falls back to its DFS path when key/dataIndex
// aren't set, so width overrides survive across renders.
function getColumnKey(col: AnyColumn, path: number[]): string {
    if (col.key !== undefined && col.key !== null) return String(col.key);
    if (
        (col as any).dataIndex !== undefined &&
        (col as any).dataIndex !== null
    ) {
        const di = (col as any).dataIndex;
        return Array.isArray(di) ? di.join('.') : String(di);
    }
    return `__path:${path.join('.')}`;
}

interface ResizeData {
    size: { width: number };
    // Index of the `<col>` that should be mutated. Resolved at dragstart
    // from the table DOM — the bottom header row alone isn't reliable
    // because rc-table places non-grouped columns (like a rowSelection
    // injected by antd) in the TOP header row with rowSpan=N, so they
    // never appear in the leaf row's `<th>` siblings.
    colIndex: number;
}

// Find the colgroup index for a header `<th>` by matching its X-position
// against a body row's `<td>`s. Each body `<td>` corresponds 1:1 to a
// `<col>` in the body colgroup, so this is correct regardless of grouped
// headers, rowSpan tricks, or columns antd injected ahead of ours.
// Scale `widths` in-place so their sum equals `target` px (within integer
// rounding). Scales down when too wide, up when narrower than the wrapper so
// the table fills the available width while header/body stay matched.
function fitWidthsToContainer(widths: number[], target: number): boolean {
    if (target <= 0) return false;
    let sum = 0;
    for (let i = 0; i < widths.length; i += 1) sum += widths[i];
    if (sum <= 0) return false;
    // Integer layout: skip only when already flush with the wrapper.
    if (Math.abs(sum - target) < 1) return false;

    const scale = target / sum;
    for (let i = 0; i < widths.length; i += 1) {
        widths[i] = Math.max(MIN_WIDTH, Math.round(widths[i] * scale));
    }
    sum = 0;
    for (let i = 0; i < widths.length; i += 1) sum += widths[i];
    const diff = target - sum;
    if (diff !== 0 && widths.length > 0) {
        const j = widths.indexOf(Math.max(...widths));
        widths[j] = Math.max(MIN_WIDTH, widths[j] + diff);
    }
    return true;
}

function resolveColIndex(th: HTMLElement): number {
    const table = th.closest('.ant-table');
    if (!table) return -1;

    const thLeft = th.getBoundingClientRect().left;

    const findClosest = (row: HTMLElement | null): number => {
        if (!row) return -1;
        let bestIdx = -1;
        let bestDiff = Infinity;
        for (let i = 0; i < row.children.length; i += 1) {
            const td = row.children[i] as HTMLElement;
            const diff = Math.abs(td.getBoundingClientRect().left - thLeft);
            if (diff < bestDiff) {
                bestDiff = diff;
                bestIdx = i;
            }
        }
        return bestIdx;
    };

    // Prefer a real body row (each `<td>` corresponds 1:1 to a `<col>` in
    // the body colgroup). Fall back to the hidden measure row when the
    // table has no data rendered.
    const dataRow = table.querySelector<HTMLElement>(
        'tbody > tr.ant-table-row',
    );
    const dataIdx = findClosest(dataRow);
    if (dataIdx >= 0) return dataIdx;

    const measureRow = table.querySelector<HTMLElement>(
        'tr.ant-table-measure-row',
    );
    return findClosest(measureRow);
}

/** Sticky layout: `table` is usually a direct child of `.ant-table-header`, but guard with a descendant query. */
function queryStickyHeaderTable(
    wrapper: HTMLElement,
): HTMLTableElement | null {
    return (
        wrapper.querySelector<HTMLTableElement>(
            '.ant-table-header > table',
        ) ?? wrapper.querySelector<HTMLTableElement>('.ant-table-header table')
    );
}

// Header cell that hosts a custom column-resize handle.
//
// We don't use `react-resizable` because its drag math is
// `width = props.width + perTickDeltaX`, which only computes the right value
// if the parent pushes the new width back as a prop on every mousemove. That
// forces a full Table re-render per tick, which is the original source of the
// stutter. Tracking width from the absolute pointer X lets us drive the drag
// entirely from `mousemove` -> direct DOM mutation, with a single React state
// commit on `mouseup`.
interface ResizableTitleProps
    extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'onResize'> {
    onResize?: (e: MouseEvent, data: ResizeData) => void;
    onResizeStop?: (e: MouseEvent, data: ResizeData) => void;
    width?: number;
    minWidth?: number;
}

const ResizableTitle: React.FC<ResizableTitleProps> = ({
    onResize,
    onResizeStop,
    width,
    minWidth,
    children,
    className,
    ...restProps
}) => {
    const thRef = useRef<HTMLTableCellElement | null>(null);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLSpanElement>) => {
            if (!width) return;
            const th = thRef.current;
            if (!th) return;
            const colIndex = resolveColIndex(th);
            if (colIndex < 0) return;

            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startWidth = width;
            const min = Math.max(MIN_WIDTH, minWidth ?? 0);

            let latestWidth = startWidth;

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const raw = startWidth + (moveEvent.clientX - startX);
                const next = Math.round(
                    Math.min(MAX_WIDTH, Math.max(min, raw)),
                );
                if (next === latestWidth) return;
                latestWidth = next;
                onResize?.(moveEvent, { size: { width: next }, colIndex });
            };

            const handleMouseUp = (upEvent: MouseEvent) => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                onResizeStop?.(upEvent, {
                    size: { width: latestWidth },
                    colIndex,
                });
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            // Keep cursor + suppress text selection across the whole drag,
            // even as the pointer leaves the header cell.
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [width, minWidth, onResize, onResizeStop],
    );

    if (!width) {
        return (
            <th ref={thRef} {...restProps} className={className}>
                {children}
            </th>
        );
    }

    return (
        <th
            ref={thRef}
            {...restProps}
            className={`${className ?? ''} react-resizable`.trim()}
        >
            {children}
            <span
                className="react-resizable-handle"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
            />
        </th>
    );
};

// Generic, typed, resizable table with forwardRef.
//
// Why this is structured the way it is:
//
//   1. We mutate the <col> width directly during the drag for instant visual
//      feedback (no React state updates per mousemove).
//
//   2. rc-table installs two ResizeObserver pathways that, by default, would
//      fire `setState`/`forceUpdate` on every drag tick — the source of the
//      stutter compared to a smaller, non-sticky table:
//
//        - per-column `MeasureCell` observers on each `<td>` inside
//          `tr.ant-table-measure-row`. When a `<col>` width changes the
//          matching measure-cell `<td>` resizes, the observer fires
//          `onColumnResize` -> `useLayoutState` -> `forceUpdate({})`.
//
//        - a `fullTable` observer on `.ant-table` itself. Resizing any
//          column changes the table's outer width, which fires
//          `onFullTableResize` -> `setComponentWidth`.
//
//      Each callback queues a full antd Table re-render. With ~50 unpaginated
//      rows × ~10 columns, that's ~500 cell reconciliations per mousemove,
//      times 2-3 forced renders, which is what makes drag feel laggy.
//
//   3. We can't cancel the polyfill's RO callbacks directly, but
//      `rc-resize-observer`'s `SingleObserver` only forwards a resize when
//      `target.getBoundingClientRect()` / `offsetWidth` / `offsetHeight`
//      report a change. So during a drag we freeze those values on the two
//      observed targets (the `.ant-table` root and every measure-row `<td>`)
//      to their pre-drag size. RO still fires; SingleObserver compares the
//      cached size to the frozen reading and no-ops.
//
//      Net result during drag: only `<col>` width mutations happen per
//      mousemove. React state is untouched, rc-table never re-renders, and
//      cell content stays put. On `mouseup` we unfreeze, commit the final
//      widths once via `setWidthOverrides`, and rc-table re-syncs normally.
//
//   4. Columns may be grouped (`{ children: [...] }`) and antd may inject
//      extra columns ahead of ours (rowSelection, expandable, etc.). When
//      both happen at once, rc-table puts the non-grouped columns in the
//      TOP header row with `rowSpan=N` — so the leaf `<th>` we drag does
//      NOT have those cells as siblings in its row, and counting siblings
//      gives the wrong colgroup index. Instead `resolveColIndex` matches
//      the dragged `<th>`'s X position against a body row's `<td>`s
//      (which always have one `<td>` per `<col>`) to recover the correct
//      colgroup index. That's what `updateColgroupWidth` mutates so body
//      cells and the sticky-header cell stay in lock-step with the header
//      `<th>` being dragged.
//
//   5. With `scroll: { x: 'max-content' }`, rc-table sets the body table's
//      style to `width: max-content`. The body's `<col>` widths come from
//      our prop widths (typically small defaults like 100/120) while the
//      sticky header's `<col>` widths come from rc-table's MEASURED widths
//      (the actual rendered cell sizes). When prop widths sum LESS than
//      the table's max-content (because content is wider than the
//      defaults), the browser proportionally scales every body col up to
//      fill the table — so a `<col style="width:100px">` may render as a
//      141px-wide `<td>`. The header doesn't scale (its col widths
//      already sum to the table width), so any `<col>` mutation we do
//      lands at exact px on the header but is partially absorbed by the
//      body's scaling. To eliminate the scaling, on mount (and when
//      `columns` changes) we measure the body's actual rendered widths
//      and seed `widthOverrides` with them. After seeding, body col
//      widths sum to the table width, scaling factor is 1, and our
//      mutations land identically on header and body.
function ResizableTableInner<RecordType extends object = any>(
    props: TableProps<RecordType>,
    ref: React.Ref<any>,
) {
    const { columns = [], ...rest } = props;

    // Width overrides keyed by the column's stable key (key / dataIndex /
    // path). Storing widths flat — instead of cloning the (possibly nested)
    // `columns` tree on every resize — keeps the state shape independent of
    // grouped columns and avoids walking arbitrary user-supplied props.
    const [widthOverrides, setWidthOverrides] = useState<
        Record<string, number>
    >({});

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const pendingWidthsRef = useRef<Map<string, number>>(new Map());
    const dragActiveRef = useRef(false);
    const sizeFreezeRef = useRef<Array<() => void>>([]);
    // Cache of the last measured `<col>` widths the sync effect applied.
    // When the next sync's measurements match exactly, the layout has
    // converged and we skip the `<col>` writes to break the ResizeObserver
    // loop that the browser warns about ("ResizeObserver loop completed
    // with undelivered notifications"). Naturally invalidates the next
    // time measurements differ — for instance after a drag commits new
    // widths into `widthOverrides`, rc-table re-emits the colgroup, body
    // cells re-layout, and the new measure-row widths won't match the
    // cache.
    const lastSyncedKeyRef = useRef<string>('');

    const updateColgroupWidth = useCallback(
        (flatIndex: number, width: number) => {
            const wrapper = wrapperRef.current;
            if (!wrapper) return;

            const bodyTable = wrapper.querySelector<HTMLTableElement>(
                '.ant-table-body > table',
            );
            const headerTable = queryStickyHeaderTable(wrapper);
            const bodyColgroup =
                bodyTable?.querySelector<HTMLTableColElement>('colgroup');
            const headerColgroup =
                headerTable?.querySelector<HTMLTableColElement>('colgroup');

            if (bodyTable) {
                bodyTable.style.setProperty('min-width', '0', 'important');
            }

            // Step 1: reflect the user's drag on the body so it can
            // re-distribute its excess space.
            const bodyColLeaf = bodyColgroup?.children[flatIndex] as
                | HTMLElement
                | undefined;
            if (bodyColLeaf) bodyColLeaf.style.width = `${width}px`;

            // Step 2: mirror the body's actual rendered cell widths
            // (post-distribution) onto BOTH colgroups so the sticky header
            // gets the SAME widths as the body. Without this the body and
            // header redistribute the excess differently per column,
            // because the browser's distribution is content-aware:
            //   - body cell `<td>` has wide content (e.g. "1,474,447") so
            //     the browser absorbs a lot of the excess into it,
            //   - header cell `<th>` has short content ("Video Views") so
            //     it absorbs almost none.
            // Same `<col>` widths but different visible widths → during-drag
            // misalignment. Reading the body's actual td widths and writing
            // them onto BOTH colgroups makes both tables end up with col
            // widths summing to the table width (no excess to distribute)
            // → cells render at exact `<col>` widths → aligned.
            //
            // We use data-row tds (not measure-row tds) because measure-row
            // tds are frozen via `freezeRcTableSizeObservers` during drag.
            const dataRow = bodyTable?.querySelector<HTMLElement>(
                'tbody > tr.ant-table-row',
            );
            if (
                !bodyColgroup ||
                !headerColgroup ||
                !dataRow ||
                !bodyTable ||
                !headerTable
            ) {
                // Fallback when the body has no data row yet (empty table,
                // first paint): just mirror the dragged col on the header.
                const headerColFallback = headerColgroup?.children[
                    flatIndex
                ] as HTMLElement | undefined;
                if (headerColFallback)
                    headerColFallback.style.width = `${width}px`;
                return;
            }

            const dataTds = Array.from(dataRow.children) as HTMLElement[];
            const bodyCols = Array.from(
                bodyColgroup.children,
            ) as HTMLElement[];
            const headerCols = Array.from(
                headerColgroup.children,
            ) as HTMLElement[];
            const matched = Math.min(
                dataTds.length,
                bodyCols.length,
                headerCols.length,
            );
            const tdStart = dataTds.length - matched;
            const bodyColStart = bodyCols.length - matched;
            const headerColStart = headerCols.length - matched;

            // Phase A: read all widths first. The first `offsetWidth`
            // access flushes the pending `<col>` mutation from step 1; all
            // subsequent reads in this loop hit the same flushed layout, so
            // the widths array is internally consistent.
            const ws = new Array<number>(matched);
            for (let i = 0; i < matched; i += 1) {
                const td = dataTds[tdStart + i];
                ws[i] = td?.offsetWidth ?? 0;
            }

            // Keep total column width equal to the wrapper: scale down if too
            // wide, scale up if narrower than the viewport so the table fills
            // the row without header/body drift.
            fitWidthsToContainer(ws, wrapper.offsetWidth);

            // Phase B: write all widths in a single batch. No reads in
            // between, so layout isn't flushed mid-loop and we don't get
            // the per-iteration drift that produced the RO oscillation in
            // the original implementation.
            for (let i = 0; i < matched; i += 1) {
                const w = ws[i];
                if (w <= 0) continue;
                const px = `${w}px`;
                const bc = bodyCols[bodyColStart + i];
                const hc = headerCols[headerColStart + i];
                if (bc) bc.style.width = px;
                if (hc) hc.style.width = px;
            }
        },
        [],
    );

    // Freeze the size readings of every element rc-table observes for column
    // / table resizes (the `.ant-table` root + each measure-row `<td>`). See
    // the long comment above this component for why this is needed. Returns
    // a teardown that restores the originals.
    const freezeRcTableSizeObservers = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const targets: HTMLElement[] = [];
        const root = wrapper.querySelector<HTMLElement>('.ant-table');
        if (root) targets.push(root);
        wrapper
            .querySelectorAll<HTMLElement>('tr.ant-table-measure-row > td')
            .forEach(td => targets.push(td));

        const teardowns: Array<() => void> = [];
        targets.forEach(el => {
            const rect = el.getBoundingClientRect();
            const ow = el.offsetWidth;
            const oh = el.offsetHeight;
            // `getBoundingClientRect` is an own method on the prototype, so
            // we have to redefine it on the instance to override.
            const hadOwnGBCR = Object.prototype.hasOwnProperty.call(
                el,
                'getBoundingClientRect',
            );
            const originalGBCR = (el as any).getBoundingClientRect;
            Object.defineProperty(el, 'getBoundingClientRect', {
                configurable: true,
                writable: true,
                value: () => rect,
            });
            Object.defineProperty(el, 'offsetWidth', {
                configurable: true,
                get: () => ow,
            });
            Object.defineProperty(el, 'offsetHeight', {
                configurable: true,
                get: () => oh,
            });
            teardowns.push(() => {
                if (hadOwnGBCR) {
                    Object.defineProperty(el, 'getBoundingClientRect', {
                        configurable: true,
                        writable: true,
                        value: originalGBCR,
                    });
                } else {
                    delete (el as any).getBoundingClientRect;
                }
                delete (el as any).offsetWidth;
                delete (el as any).offsetHeight;
            });
        });
        sizeFreezeRef.current = teardowns;
    }, []);

    const unfreezeRcTableSizeObservers = useCallback(() => {
        const teardowns = sizeFreezeRef.current;
        sizeFreezeRef.current = [];
        teardowns.forEach(fn => {
            try {
                fn();
            } catch {
                // ignore: element may have been unmounted mid-drag
            }
        });
    }, []);

    const handleResize = useCallback(
        (key: string) =>
            (_e: MouseEvent, { size, colIndex }: ResizeData) => {
                if (!dragActiveRef.current) {
                    dragActiveRef.current = true;
                    // Freeze BEFORE the first <col> mutation so the cached
                    // sizes captured here pre-date the resize. Subsequent RO
                    // fires will diff against this frozen reading and no-op.
                    freezeRcTableSizeObservers();
                }
                pendingWidthsRef.current.set(key, size.width);
                // Pure DOM mutation — no React state changes. With the size
                // observers frozen above, rc-table's internal forceUpdates
                // are suppressed, so this is the only work that happens per
                // mousemove tick.
                updateColgroupWidth(colIndex, size.width);
            },
        [freezeRcTableSizeObservers, updateColgroupWidth],
    );

    const handleResizeStop = useCallback(
        (key: string) =>
            (_e: MouseEvent, { size }: ResizeData) => {
                pendingWidthsRef.current.set(key, size.width);
                const pending = pendingWidthsRef.current;

                dragActiveRef.current = false;
                // Restore size readings BEFORE committing state so the
                // resulting render lets rc-table observe accurate sizes again.
                unfreezeRcTableSizeObservers();

                if (pending.size === 0) return;

                setWidthOverrides(prev => {
                    let changed = false;
                    const next = { ...prev };
                    pending.forEach((width, k) => {
                        if (next[k] !== width) {
                            next[k] = width;
                            changed = true;
                        }
                    });
                    pending.clear();
                    return changed ? next : prev;
                });
            },
        [unfreezeRcTableSizeObservers],
    );

    // Safety net: if the component unmounts mid-drag, make sure we don't
    // leave per-instance property overrides behind on detached DOM nodes.
    React.useEffect(
        () => () => {
            unfreezeRcTableSizeObservers();
        },
        [unfreezeRcTableSizeObservers],
    );

    // Walks the (possibly nested) columns, applies any width overrides on
    // leaves, and attaches resize handlers ONLY to leaf header cells. Group
    // headers don't get a handle — their visual width is the sum of their
    // children's `<col>` widths, which already follow naturally.
    //
    // The `<col>` index is resolved from the dragged `<th>`'s DOM position
    // at dragstart (see `ResizableTitle`), so we don't need to track it
    // here — that's the only way to stay correct when antd injects extra
    // columns ahead of ours (rowSelection / expandable / etc.).
    const resizableColumns = useMemo(() => {
        const transform = (
            cols: AnyColumn[],
            path: number[],
        ): AnyColumn[] =>
            cols.map((col, i) => {
                const cp = [...path, i];
                if (col.children && col.children.length > 0) {
                    return {
                        ...col,
                        children: transform(col.children, cp),
                    };
                }
                const key = getColumnKey(col, cp);
                const width =
                    widthOverrides[key] ??
                    (typeof col.width === 'number'
                        ? col.width
                        : DEFAULT_WIDTH);
                const userShouldCellUpdate = (col as ColumnType<RecordType>)
                    .shouldCellUpdate;
                return {
                    ...col,
                    width,
                    shouldCellUpdate: (
                        nextRecord: RecordType,
                        prevRecord: RecordType,
                    ) => {
                        if (dragActiveRef.current) return false;
                        if (userShouldCellUpdate) {
                            return userShouldCellUpdate(
                                nextRecord,
                                prevRecord,
                            );
                        }
                        return nextRecord !== prevRecord;
                    },
                    onHeaderCell: () =>
                        ({
                            width,
                            minWidth: (col as any).minWidth,
                            onResize: handleResize(key),
                            onResizeStop: handleResizeStop(key),
                        }) as any,
                };
            });

        return transform(columns as AnyColumn[], []) as ColumnsType<RecordType>;
    }, [columns, widthOverrides, handleResize, handleResizeStop]);

    // Ordered leaf column keys (DFS), used to map measured `<td>` widths
    // back to widthOverrides entries. Recomputed only when `columns`
    // changes — never during a drag.
    const leafKeys = useMemo(() => {
        const keys: string[] = [];
        const walk = (cols: AnyColumn[], path: number[]) => {
            cols.forEach((col, i) => {
                const cp = [...path, i];
                if (col.children && col.children.length > 0) {
                    walk(col.children, cp);
                } else {
                    keys.push(getColumnKey(col, cp));
                }
            });
        };
        walk(columns as AnyColumn[], []);
        return keys;
    }, [columns]);

    // Seed `widthOverrides` from the body's actual rendered `<td>` widths
    // after mount and whenever columns change. Without this, the body's
    // `<col>` widths (taken from prop widths, often small defaults) sum
    // to LESS than the body table's `width: max-content`, and the browser
    // proportionally scales the body cells to fill the table — causing
    // the header (which uses measured widths and renders 1:1) to visually
    // diverge from the body whenever we mutate a single `<col>`. With the
    // measured widths seeded, body col widths sum equals the table width,
    // scale factor is 1, and resizes land identically on header and body.
    const hasSeededRef = useRef(false);
    useLayoutEffect(() => {
        hasSeededRef.current = false;
    }, [leafKeys]);
    useEffect(() => {
        if (hasSeededRef.current) return;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const dataRow = wrapper.querySelector<HTMLElement>(
            'tbody > tr.ant-table-row',
        );
        if (!dataRow) return;
        const tdEls = Array.from(dataRow.children) as HTMLElement[];
        // Body row may have antd-injected leading cells (rowSelection,
        // expandable). Our leaf columns map onto the trailing tds.
        const offset = tdEls.length - leafKeys.length;
        if (offset < 0) return;

        const measured: Record<string, number> = {};
        leafKeys.forEach((key, i) => {
            const td = tdEls[i + offset];
            if (!td) return;
            const w = Math.round(td.getBoundingClientRect().width);
            if (w > 0) measured[key] = w;
        });

        if (Object.keys(measured).length === 0) return;
        hasSeededRef.current = true;
        setWidthOverrides(prev => {
            // Only seed keys that haven't been explicitly set by a user
            // resize, so subsequent re-renders don't clobber custom widths.
            let changed = false;
            const next = { ...prev };
            Object.entries(measured).forEach(([k, w]) => {
                if (next[k] === undefined) {
                    next[k] = w;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    });

    // Force the sticky header and the body to use IDENTICAL `<col>` widths.
    //
    // Why JS rather than CSS:
    //
    //   - rc-table sets the body `<table>` to
    //       `width: max-content; min-width: 100%; tableLayout: fixed`
    //     inline. `min-width: 100%` forces the body to fill its scroll
    //     container; when the sum of `<col>` widths is smaller than the
    //     container, the browser scales every `<col>` up proportionally to
    //     fill. The sticky header `<table>` has no `min-width`, so it
    //     stays at the natural sum and the two tables drift apart.
    //
    //   - The header's `<col>` widths are populated from rc-table's
    //     `colsWidths` state, which is updated from a hidden `MeasureRow`'s
    //     `offsetWidth` callbacks. Those values are rounded integers and
    //     can lag the body's actual layout by a frame, which compounds the
    //     drift across columns from left to right.
    //
    // We side-step both by:
    //   1. Killing the body's `min-width: 100%` on the inline style so it
    //      sizes to `max-content` (= sum of `<col>` widths under
    //      `table-layout: fixed`). Same for the header.
    //   2. Reading the body's data-row `<td>` widths post-layout (these
    //      are the ground truth for what the user sees).
    //   3. Writing those widths back onto BOTH the body's and the header's
    //      `<col>` styles so the two colgroups are byte-identical.
    //
    // We deliberately do NOT pin an explicit `width: <sum>px` on the
    // `<table>`s — that would prevent the tables from naturally re-sizing
    // when `<col>` widths are mutated during a drag. Letting them stay at
    // `max-content` (with `min-width: 0`) lets each mousemove tick reflow
    // both tables to the new sum without any extra bookkeeping.
    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;

        const syncWidths = () => {
            // Skip during a drag — `updateColgroupWidth` already keeps
            // header + body in lockstep per mousemove tick, and reading
            // getBoundingClientRect here would defeat the size-observer
            // freeze that keeps the drag smooth.
            if (dragActiveRef.current) return;

            const bodyTable = wrapper.querySelector<HTMLTableElement>(
                '.ant-table-body > table',
            );
            const headerTable = queryStickyHeaderTable(wrapper);
            if (!bodyTable || !headerTable) return;

            // rc-table sets `min-width: 100%` on the body table so it fills the
            // scroll area; the browser then grows columns by content. The sticky
            // header table does not get the same distribution. Clear that min-width
            // before measuring, then mirror the body's cell widths onto both colgroups.
            bodyTable.style.setProperty('min-width', '0', 'important');
            const measureRow = bodyTable.querySelector<HTMLElement>(
                'tr.ant-table-measure-row',
            );
            const dataRow = bodyTable.querySelector<HTMLElement>(
                'tbody > tr.ant-table-row',
            );
            const widthRow = dataRow ?? measureRow;

            const bodyColgroup =
                bodyTable.querySelector<HTMLTableColElement>('colgroup');
            const headerColgroup =
                headerTable.querySelector<HTMLTableColElement>('colgroup');
            if (!widthRow || !bodyColgroup || !headerColgroup) return;

            const widthCells = Array.from(
                widthRow.children,
            ) as HTMLElement[];
            const bodyCols = Array.from(
                bodyColgroup.children,
            ) as HTMLElement[];
            const headerCols = Array.from(
                headerColgroup.children,
            ) as HTMLElement[];

            // Align colgroups from the trailing edge so any antd-injected
            // leading cells (rowSelection, expandable) don't shift the
            // mapping when one side has them and the other doesn't.
            const matched = Math.min(
                widthCells.length,
                bodyCols.length,
                headerCols.length,
            );
            const tdStart = widthCells.length - matched;
            const bodyColStart = bodyCols.length - matched;
            const headerColStart = headerCols.length - matched;

            // Phase A: read every td width before mutating any `<col>`. The
            // first `offsetWidth` access flushes any pending layout from a
            // previous render, and every subsequent read in this loop hits
            // the same layout state, so the resulting `ws` array is
            // internally consistent. Doing read+write per iteration (the
            // original implementation) caused 1-3px drift per column
            // because each `<col>` write invalidated layout, the next
            // `offsetWidth` read forced a flush, and that flush returned a
            // slightly different value than the one we'd just used to size
            // the previous `<col>`. That drift then re-triggered the
            // ResizeObserver which re-ran sync — the source of the
            // "ResizeObserver loop completed with undelivered notifications"
            // browser warning.
            const ws = new Array<number>(matched);
            for (let i = 0; i < matched; i += 1) {
                const td = widthCells[tdStart + i];
                ws[i] = td?.offsetWidth ?? 0;
            }

            // Match total table width to the wrapper (narrower → grow columns,
            // wider → shrink) so the layout fills the available width.
            fitWidthsToContainer(ws, wrapper.offsetWidth);

            // Phase B: write all `<col>` widths in one batch when the fingerprint
            // changed — skip redundant writes to avoid ResizeObserver thrash.
            const widthsKey = `${wrapper.offsetWidth}:${ws
                .map(w => w.toFixed(2))
                .join(',')}`;
            if (widthsKey !== lastSyncedKeyRef.current) {
                lastSyncedKeyRef.current = widthsKey;
                for (let i = 0; i < matched; i += 1) {
                    const w = ws[i];
                    if (w <= 0) continue;
                    const px = `${w}px`;
                    const bodyCol = bodyCols[bodyColStart + i];
                    const headerCol = headerCols[headerColStart + i];
                    if (bodyCol) bodyCol.style.width = px;
                    if (headerCol) headerCol.style.width = px;
                }
            }

            // Pin both tables to the same total width (sum of fitted `<col>`s).
            // Ant Design's `table { width: 100% }` on the header must not scale
            // columns differently from the body scroll table.
            void bodyTable.offsetHeight;
            const tableTotal = ws.reduce((acc, w) => acc + w, 0);
            if (tableTotal > 0) {
                const px = `${tableTotal}px`;
                bodyTable.style.setProperty('width', px, 'important');
                bodyTable.style.setProperty('min-width', '0', 'important');
                headerTable.style.setProperty('width', px, 'important');
                headerTable.style.setProperty('min-width', '0', 'important');
            }
        };

        syncWidths();

        // Re-sync on:
        //   - body cell size changes (window resize, content reflow)
        //   - header cell size changes (rc-table's internal RO can update
        //     `colsWidths` and re-render only the header without our
        //     component re-rendering, leaving our last sync stale)
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        let rafId = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(syncWidths);
        });
        const bodyRow = wrapper.querySelector<HTMLElement>(
            'tbody > tr.ant-table-row',
        );
        const headerRow = wrapper.querySelector<HTMLElement>(
            '.ant-table-thead > tr',
        );
        if (bodyRow) {
            Array.from(bodyRow.children).forEach(td =>
                ro.observe(td as Element),
            );
        } else {
            const bodyScrollTable = wrapper.querySelector<HTMLTableElement>(
                '.ant-table-body > table',
            );
            const measureRowForRo =
                bodyScrollTable?.querySelector<HTMLElement>(
                    'tr.ant-table-measure-row',
                );
            if (measureRowForRo) {
                Array.from(measureRowForRo.children).forEach(td =>
                    ro.observe(td as Element),
                );
            }
        }
        if (headerRow) {
            Array.from(headerRow.children).forEach(th =>
                ro.observe(th as Element),
            );
        }
        return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
    });

    const components = useMemo(
        () => ({
            header: {
                cell: ResizableTitle,
            },
        }),
        [],
    );

    return (
        <div ref={wrapperRef} className="resizable-table-wrapper">
            <Table
                ref={ref}
                {...rest}
                columns={resizableColumns}
                components={components}
            />
        </div>
    );
}

const ResizableTable = forwardRef(ResizableTableInner) as <
    RecordType extends object = any,
>(
    props: TableProps<RecordType> & { ref?: React.Ref<any> },
) => React.ReactElement;

export default ResizableTable;
